// Runtime font metrics, so text layout can be based on what each font actually draws
// instead of per-font magic numbers. Values are ratios of the font size.
const SAMPLE_SIZE = 100;
const FALLBACK_METRICS = { ascent: 0.95, descent: 0.25, capHeight: 0.7, descender: 0.2 };
const cache = {};
let context = null;

export const getTypoScale = (typo) => {
    const match = /scale-\[?(\d+)/.exec(typo?.scale || '');
    return match ? Number(match[1]) / 100 : 1;
};

export async function getFontMetrics(element) {
    const { fontStyle, fontWeight, fontFamily } = window.getComputedStyle(element);
    const font = `${fontStyle} ${fontWeight} ${SAMPLE_SIZE}px ${fontFamily}`;
    if (cache[font]) {
        return cache[font];
    }

    let loaded = false;
    try {
        await document.fonts.load(font, 'H');
        loaded = document.fonts.check(font, 'H');
    } catch (e) {}

    context = context || document.createElement('canvas').getContext('2d');
    context.font = font;
    const measured = context.measureText('H');
    const metrics = {
        ascent: (measured.fontBoundingBoxAscent ?? FALLBACK_METRICS.ascent * SAMPLE_SIZE) / SAMPLE_SIZE,
        descent: (measured.fontBoundingBoxDescent ?? FALLBACK_METRICS.descent * SAMPLE_SIZE) / SAMPLE_SIZE,
        capHeight: measured.actualBoundingBoxAscent / SAMPLE_SIZE || FALLBACK_METRICS.capHeight,
        // how far real glyphs (g, j, p, q, y) drop below the baseline; script fonts go much deeper
        descender: context.measureText('gjpqy').actualBoundingBoxDescent / SAMPLE_SIZE || FALLBACK_METRICS.descender,
    };

    // keep measuring until the real font has arrived, otherwise we'd cache the fallback font
    if (loaded) {
        cache[font] = metrics;
    }
    return metrics;
}

// Sizes a text block so its box starts at the cap height of the first line and ends on the
// baseline of the last one (what CSS `text-box: trim-both cap alphabetic` does, which
// html2canvas doesn't support). Line spacing is expressed in cap heights, so it looks the same
// for every font regardless of its internal ascent/descent padding.
export function trimmedTextStyle(metrics, fontSize, capLineHeight, spaceAbove = 0) {
    const capHeight = metrics.capHeight * fontSize;
    const lineHeight = capHeight * capLineHeight;
    const halfLeading = (lineHeight - (metrics.ascent + metrics.descent) * fontSize) / 2;

    return {
        fontSize: `${fontSize}px`,
        lineHeight: `${lineHeight}px`,
        marginTop: `${spaceAbove - (halfLeading + metrics.ascent * fontSize - capHeight)}px`,
        marginBottom: `${-(halfLeading + metrics.descent * fontSize)}px`,
    };
}
